import * as React from 'react'
import * as ReactDOM from 'react-dom/client'
import * as Redux from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { Store, AnyAction } from 'redux'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/latest'
import * as consent from '@owl-nest/consent'
import * as env from '@owl-nest/config'
import * as error from '@owl-nest/error'
import * as fb from '@owl-nest/facebook-sdk-init'
import * as logger from '@owl-nest/logger'
import * as messages from '@owl-nest/messages'
import * as model from '@owl-nest/models'
import * as services from '@owl-nest/services'
import * as shadow from '@owl-nest/shadow'
import { State } from '@owl-nest/redux-wrapper'
import { initializeLocalization, t } from '@owl-nest/localize'

import * as projectModel from './public/models/project'
import { AppState, RootState } from './reducers'
import { createApplicationStore } from './store'
import * as UFE from './UFE'

import { MainRouter } from './MainRouter'

init()

async function init(): Promise<void> {
  await env.initUserConfig()
  const userConfig = env.getUserConfig()

  logger.init({
    dsn: env.SENTRY_PUBLIC_DSN,
    environment: env.ENVIRONMENT,
    user: {
      country: env.USER_COUNTRY,
      currency: userConfig.currency,
      email: UFE.CURRENT_USER.email,
      id: UFE.CURRENT_USER.id,
      isActive: UFE.CURRENT_USER.is_active,
      isGuest: UFE.CURRENT_USER.is_guest,
      locale: userConfig.lang,
    },
  })
  logger.setTag('feature', 'project-page')
  logger.setTag('project', UFE.PROJECT.id)

  fb.init({ appId: env.FACEBOOK_APP_ID ?? '', locale: api.EXTENDED_LOCALES[userConfig.lang] })

  // get project page "access token" from search params, and rewrite the url to remove it
  const urlSearchParams = new URLSearchParams(window.location.search)
  let token = urlSearchParams.get('token') || undefined
  if (token !== undefined) {
    const url = new URL(window.location.href)
    urlSearchParams.delete('token')
    url.search = urlSearchParams.toString()
    history.replaceState(null, document.title, url.toString())
  }

  await initializeLocalization(userConfig.lang, env.LOCALE_FILES[userConfig.lang])

  consent.whenGivenFor(consent.Vendor.ZENDESK, {
    do: () => window.zE && window.zE('webWidget', 'updateSettings', { analytics: true, cookies: true }),
    otherwise: () => window.zE && window.zE('webWidget', 'updateSettings', { analytics: false, cookies: false }),
  })

  let initialError
  const response = await services.project
    .load(UFE.PROJECT.slug || String(UFE.PROJECT.id), {
      withToken: UFE.CURRENT_USER.is_authenticated,
      projectToken: token,
      extraFields: model.project.OWNER_EXTRA_FIELDS,
      identifier: UFE.PROJECT.slug ? 'slug' : undefined,
    })
    .catch((error) => {
      initialError = error
    })

  const project = response && response.isRight() ? response.unwrap() : undefined
  if (!project) {
    return handleGlobalError(initialError, response)
  }

  // if project fetched with the "access token" is not online and has no read
  // permission, then the access token is invalid and should be discarded.
  if (
    project.status !== api.ProjectStatus.ONLINE &&
    !model.project.hasSelfPermission<projectModel.ExtraFields>(project, api.ProjectSelfPermission.READ)
  ) {
    token = undefined
  }

  if (UFE.REQUEST.path?.indexOf('impact') === -1) {
    if (
      model.project.video<projectModel.ExtraFields>(project)?.provider_name?.toLowerCase() === 'youtube' ||
      UFE.CURRENT_USER?.id === project.owner.id
    ) {
      // FIXME: This needs to be moved upper on the workflow AND be subject to consent
      // NOTE: Load `iframe_api` to control YouTube players when needed
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      document.body.appendChild(script)
    }
  }

  const store = createApplicationStore(project, token)

  // reactive bind page title to project name. Reactivity needed because project if re-fetched during the application lifetime (on login in the comment section/comming soon like)
  store.subscribe(() => {
    const state = store.getState()
    const project = state.application.project.data
    document.title = !model.project.isCancelled(project)
      ? (model.project.name<projectModel.ExtraFields>(project) ?? t('Untitled Project'))
      : t('Archived project')
  })

  const isComingSoonEnabled = model.project.isComingSoonEnabled(project)
  if (isComingSoonEnabled) {
    document.title = `${model.project.name(project)} - Ulule`
  }

  renderApp(store)
}

function renderApp(store: Store<State<AppState>, AnyAction>): void {
  const appContainer = document.querySelector('#js-project-detail')
  if (appContainer) {
    const root = ReactDOM.createRoot(appContainer)
    root.render(
      <Redux.Provider store={store}>
        <App />
      </Redux.Provider>,
    )
  } else {
    logger.err('[project] App container is missing')
  }
}

function App(): React.ReactElement<unknown> {
  const auth = services.user.useAuth()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const slug = UFE.PROJECT.slug || UFE.PROJECT.id
  const basename = UFE.REQUEST.path?.startsWith(`/projects/${slug}`) ? `/projects/${slug}/` : `/${slug}/`

  React.useEffect(() => {
    if (auth.type === 'loggedin' && project.user_role === api.UserRole.Owner) {
      if (window.zE) {
        window.zE('webWidget', 'hide')
      }
    }
  }, [auth.type, project])

  return (
    <shadow.TrackingProvider
      getUserId={() => (auth.type === 'loggedin' ? auth.user.id : undefined)}
      gtmContainerId={UFE.GTM_KEY}
      pageCategory="project"
      siteCategory={shadow.SITE.ULULE}
      project={project as api.Project<'analytics'>}
    >
      <messages.MessagesProvider>
        <plume.LockScrollGlobalStyle />
        <BrowserRouter basename={basename}>
          <MainRouter />
        </BrowserRouter>
      </messages.MessagesProvider>
    </shadow.TrackingProvider>
  )
}

// TODO: Move somewhere it can be easily visible and reused
declare global {
  interface Window {
    zE?: {
      (type: 'webWidget:on' | 'webWidget' | 'webWidget:get', command: string, payload?: unknown): void
      (type: 'webWidget', command: 'updateSettings', payload: Record<string, unknown>): void
    }
  }
}

function handleGlobalError(
  initialError: any,
  response: api.HttpResult<api.Project<model.project.ProjectOwnerExtraFields>> | void,
) {
  let unwrappedResponse
  if (response) {
    unwrappedResponse = response.isLeft() ? response.unwrapLeft() : undefined
  }
  const message = Array.isArray(unwrappedResponse?.body)
    ? unwrappedResponse.body[0].message
    : unwrappedResponse?.body?.message
  const reason = (initialError ? initialError : message) ?? 'Could not determine reason'
  error.renderCrashToast('project', reason, {
    response,
  })
}
